import React, { useEffect, useState } from 'react';
import { HiOutlineUserAdd, HiOutlineUserRemove } from 'react-icons/hi';
import SearchFilter from '../../../atoms/SearchFilter';
import StatusReasonModal from '../StatusReasonModal';
import communicationApi, { DirectoryUser } from '../../../../services/communicationApi';
import { Group } from '../../../../types/communication';
import { UserAvatar, userEmail, userName } from './UserAvatar';

interface GroupMembersTabProps {
    group: Group;
    disabled: boolean;
    onAdd: (login: string) => Promise<boolean>;
    onRemove: (login: string, reason: string) => Promise<boolean>;
}

const formatDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('pt-BR') : '—');

const GroupMembersTab: React.FC<GroupMembersTabProps> = ({ group, disabled, onAdd, onRemove }) => {
    const [term, setTerm] = useState('');
    const [removeTarget, setRemoveTarget] = useState<string | null>(null);
    const [results, setResults] = useState<DirectoryUser[]>([]);
    const members = group.users.filter((u) => u.active);
    const former = group.users.filter((u) => !u.active);

    useEffect(() => {
        const value = term.trim();
        if (!value) {
            setResults([]);
            return;
        }
        let active = true;
        const handle = setTimeout(() => {
            communicationApi
                .searchUsers(value)
                .then((users) => {
                    if (active) setResults(users);
                })
                .catch(() => {
                    if (active) setResults([]);
                });
        }, 400);
        return () => {
            active = false;
            clearTimeout(handle);
        };
    }, [term]);

    const current = new Set(members.map((u) => u.login));
    const candidates = results.filter((u) => !current.has(u.username));

    return (
        <div className="flex flex-col gap-4">
            {!disabled && (
                <div className="relative">
                    <SearchFilter value={term} onChange={setTerm} placeholder="Adicionar pessoa por nome ou e-mail..." />
                    {candidates.length > 0 && (
                        <ul className="absolute z-20 w-full mt-1 rounded-lg border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-xl max-h-56 overflow-y-auto">
                            {candidates.map((u) => (
                                <li key={u.username}>
                                    <button
                                        type="button"
                                        onClick={async () => {
                                            if (await onAdd(u.username)) setTerm('');
                                        }}
                                        className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-slate-50 dark:hover:bg-slate-700"
                                    >
                                        <UserAvatar login={u.username} />
                                        <span className="flex-1 min-w-0">
                                            <span className="block text-sm text-gray-800 dark:text-gray-100">{u.name ?? u.username}</span>
                                            <span className="block text-[11px] text-gray-400 truncate">{u.email ?? u.username}</span>
                                        </span>
                                        <HiOutlineUserAdd className="text-teal-600" size={18} />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}

            {members.length ? (
                <ul className="divide-y divide-gray-100 dark:divide-slate-700">
                    {members.map((u) => (
                        <li key={u.login} className="flex items-center gap-3 py-2.5">
                            <UserAvatar login={u.login} size="md" />
                            <div className="flex-1 min-w-0">
                                <div className="text-sm text-gray-800 dark:text-gray-100">{userName(u.login)}</div>
                                <div className="text-[11px] text-gray-400 truncate">
                                    {userEmail(u.login) ?? u.login} · desde {formatDate(u.joinedAt)}
                                </div>
                            </div>
                            {!disabled && (
                                <button
                                    type="button"
                                    title="Remover do grupo"
                                    onClick={() => setRemoveTarget(u.login)}
                                    className="text-gray-400 hover:text-red-600"
                                >
                                    <HiOutlineUserRemove size={18} />
                                </button>
                            )}
                        </li>
                    ))}
                </ul>
            ) : (
                <div className="rounded-xl border-2 border-dashed border-gray-200 dark:border-slate-700 p-6 text-center text-sm text-gray-500">
                    Ninguém no grupo ainda. Busque acima para adicionar.
                </div>
            )}

            {former.length > 0 && (
                <details className="rounded-lg bg-slate-50 dark:bg-slate-900 px-3 py-2">
                    <summary className="text-xs text-gray-500 cursor-pointer">Saíram do grupo ({former.length})</summary>
                    <ul className="mt-2 flex flex-col gap-1.5">
                        {former.map((u) => (
                            <li key={u.login} className="text-xs text-gray-500">
                                <span className="text-gray-700 dark:text-gray-300">{userName(u.login)}</span> — em {formatDate(u.removedAt)}
                                {u.removalReason && <span className="italic"> · “{u.removalReason}”</span>}
                            </li>
                        ))}
                    </ul>
                </details>
            )}

            <StatusReasonModal
                isOpen={Boolean(removeTarget)}
                title={`Remover ${removeTarget ? userName(removeTarget) : ''} do grupo`}
                actionLabel="Remover"
                onCancel={() => setRemoveTarget(null)}
                onConfirm={async (reason) => {
                    if (removeTarget) await onRemove(removeTarget, reason);
                    setRemoveTarget(null);
                }}
            />
        </div>
    );
};

export default GroupMembersTab;
